// Atlas Service - Real implementation for location CRUD operations
import { supabase } from './supabase';
import { Location, LocationType, Item } from '../types';

export const AtlasService = {
    /**
     * Get all locations for an idea
     */
    getLocations: async (ideaId: string): Promise<Location[]> => {
        const { data, error } = await supabase
            .from('locations')
            .select('*')
            .eq('idea_id', ideaId)
            .order('created_at', { ascending: true });

        if (error) {
            console.error('Error fetching locations:', error);
            return [];
        }
        return data || [];
    },

    /**
     * Get locations with hierarchy (parent/children)
     */
    getLocationsHierarchy: async (ideaId: string): Promise<Location[]> => {
        const { data, error } = await supabase
            .from('locations')
            .select('*')
            .eq('idea_id', ideaId)
            .is('parent_location_id', null)
            .order('created_at', { ascending: true });

        if (error) {
            console.error('Error fetching locations hierarchy:', error);
            return [];
        }

        // Get children for each location
        const locationsWithChildren = await Promise.all(
            (data || []).map(async (location) => {
                const { data: children } = await supabase
                    .from('locations')
                    .select('*')
                    .eq('parent_location_id', location.id)
                    .order('created_at', { ascending: true });
                return { ...location, children: children || [] };
            })
        );

        return locationsWithChildren;
    },

    /**
     * Get a single location by ID
     */
    getLocation: async (id: string): Promise<Location | null> => {
        const { data, error } = await supabase
            .from('locations')
            .select('*')
            .eq('id', id)
            .single();

        if (error) {
            console.error('Error fetching location:', error);
            return null;
        }
        return data;
    },

    /**
     * Create a new location
     */
    createLocation: async (
        ideaId: string,
        name: string,
        description?: string,
        type: string = 'exterior',
        parentId?: string
    ): Promise<Location | null> => {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) {
            console.error('No authenticated user');
            return null;
        }

        const { data, error } = await supabase
            .from('locations')
            .insert({
                idea_id: ideaId,
                user_id: user.id,
                name,
                description,
                type: type as LocationType,
                parent_location_id: parentId || null
            })
            .select()
            .single();

        if (error) {
            console.error('Error creating location:', error);
            return null;
        }
        return data;
    },

    /**
     * Update a location
     */
    updateLocation: async (id: string, updates: Partial<Location>): Promise<Location | null> => {
        // Remove read-only fields
        const { id: _id, user_id, created_at, children, plot_points, ...safeUpdates } = updates as any;

        const { data, error } = await supabase
            .from('locations')
            .update({
                ...safeUpdates,
                updated_at: new Date().toISOString()
            })
            .eq('id', id)
            .select()
            .single();

        if (error) {
            console.error('Error updating location:', error);
            return null;
        }
        return data;
    },

    /**
     * Delete a location
     */
    deleteLocation: async (id: string): Promise<boolean> => {
        const { error } = await supabase
            .from('locations')
            .delete()
            .eq('id', id);

        if (error) {
            console.error('Error deleting location:', error);
            return false;
        }
        return true;
    },

    /**
     * Get placed locations (those with canvas coordinates)
     */
    getPlacedLocations: async (ideaId: string): Promise<Location[]> => {
        const { data, error } = await supabase
            .from('locations')
            .select('*')
            .eq('idea_id', ideaId)
            .not('canvas_x', 'is', null)
            .not('canvas_y', 'is', null)
            .order('created_at', { ascending: true });

        if (error) {
            console.error('Error fetching placed locations:', error);
            return [];
        }
        return data || [];
    },

    /**
     * Get unplaced locations (text-only, no canvas coordinates)
     */
    getUnplacedLocations: async (ideaId: string): Promise<Location[]> => {
        const { data, error } = await supabase
            .from('locations')
            .select('*')
            .eq('idea_id', ideaId)
            .or('canvas_x.is.null,canvas_y.is.null')
            .order('created_at', { ascending: true });

        if (error) {
            console.error('Error fetching unplaced locations:', error);
            return [];
        }
        return data || [];
    },

    /**
     * Place a location on the canvas
     */
    placeOnCanvas: async (
        id: string,
        x: number,
        y: number,
        imageUrl: string,
        width: number = 200,
        height: number = 200
    ): Promise<Location | null> => {
        return AtlasService.updateLocation(id, {
            canvas_x: x,
            canvas_y: y,
            canvas_width: width,
            canvas_height: height,
            atlas_image_url: imageUrl
        });
    },

    /**
     * Remove a location from canvas (keeps the text data)
     */
    removeFromCanvas: async (id: string): Promise<Location | null> => {
        return AtlasService.updateLocation(id, {
            canvas_x: null,
            canvas_y: null,
            atlas_image_url: null
        });
    },

    /**
     * Move a location on the canvas
     */
    moveOnCanvas: async (id: string, x: number, y: number): Promise<Location | null> => {
        return AtlasService.updateLocation(id, {
            canvas_x: x,
            canvas_y: y
        });
    },

    /**
     * Resize a location block
     */
    resizeOnCanvas: async (id: string, width: number, height: number): Promise<Location | null> => {
        return AtlasService.updateLocation(id, {
            canvas_width: width,
            canvas_height: height
        });
    },

    // Legacy Item methods (kept for backward compatibility with WorldBuildingPanel)
    getItems: async (ideaId: string): Promise<Item[]> => {
        const { data, error } = await supabase
            .from('items')
            .select('*')
            .eq('idea_id', ideaId)
            .order('created_at', { ascending: true });

        if (error) {
            console.error('Error fetching items:', error);
            return [];
        }
        return data || [];
    },

    createItem: async (name: string, type: string, ideaId: string): Promise<Item | null> => {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return null;

        const { data, error } = await supabase
            .from('items')
            .insert({
                idea_id: ideaId,
                user_id: user.id,
                name,
                type
            })
            .select()
            .single();

        if (error) {
            console.error('Error creating item:', error);
            return null;
        }
        return data;
    }
};
